import { Loader } from "@mantine/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { clientsApi } from "#/api/clientsApi";
import { prvApi } from "#/api/prvApi";
import { EmptyState } from "#/components/ui/EmptyState";
import ReportSection from "#/pages/reports/components/ReportSection";
import { type PrvRun, PrvRunStatus } from "#/types/prv";
import { logger } from "#/utils/logger";
import PrvConfigPanel from "./PrvConfigPanel";
import PrvResultsDrawer from "./PrvResultsDrawer";
import PrvRunLogTable, { PRV_RUNS_QUERY_KEY } from "./PrvRunLogTable";
import PrvRunModal from "./PrvRunModal";

interface Props {
  clientId: number;
}

/**
 * TMA-admin-only "Product Rotation (PRV)" tab on Client Detail. Left: run
 * log. Right: configuration + Run now / View patterns. A manual run opens the
 * progress modal; on completion the results drawer opens. A run already in
 * progress (e.g. after a page reload) is picked up automatically. The
 * Logistics Prediction built on this engine has its own tab.
 */
const PrvEngineTab = ({ clientId }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [followedRunId, setFollowedRunId] = useState<number | null>(null);
  const [resultsOpened, setResultsOpened] = useState(false);
  /** Runs already handed over / dismissed, so a stale "active run" answer never reopens them. */
  const handledRuns = useRef(new Set<number>());

  const clientQuery = useQuery({
    queryKey: ["client", clientId, "prv"],
    queryFn: async () => (await clientsApi.getById(clientId)).data,
  });

  const latestRunQuery = useQuery({
    queryKey: [PRV_RUNS_QUERY_KEY, clientId, "latest"],
    queryFn: async () => {
      const response = await prvApi.listRuns(clientId, {
        page: 1,
        limit: 1,
        sortBy: "startedAt",
        order: "DESC",
      });
      return response.data.data[0] ?? null;
    },
  });

  const completedRunQuery = useQuery({
    queryKey: [PRV_RUNS_QUERY_KEY, clientId, "latest-completed"],
    queryFn: async () => {
      const response = await prvApi.listRuns(clientId, {
        page: 1,
        limit: 1,
        sortBy: "startedAt",
        order: "DESC",
        filterBy: `status=${PrvRunStatus.COMPLETED}`,
      });
      return response.data.data[0] ?? null;
    },
  });

  // Resume following a run that is still going (page reload, tab reopened).
  const activeRunQuery = useQuery({
    queryKey: ["prv-active-run", clientId],
    queryFn: async () => (await prvApi.getActiveRun(clientId)).data.run,
  });
  useEffect(() => {
    const active = activeRunQuery.data;
    if (
      active &&
      active.status === PrvRunStatus.RUNNING &&
      followedRunId === null &&
      !handledRuns.current.has(active.id)
    ) {
      setFollowedRunId(active.id);
    }
  }, [activeRunQuery.data, followedRunId]);

  const refreshRuns = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [PRV_RUNS_QUERY_KEY, clientId] });
    void queryClient.invalidateQueries({ queryKey: ["prv-active-run", clientId] });
  }, [queryClient, clientId]);

  const startRun = useMutation({
    mutationFn: () => prvApi.startRun(clientId),
    onSuccess: (response) => {
      setFollowedRunId(response.data.id);
      refreshRuns();
    },
    onError: (error: Error) => {
      // The API client already toasts the message (e.g. 409 "run in progress").
      logger.error("PrvEngineTab startRun error:", error);
      refreshRuns();
    },
  });

  const handleCompleted = useCallback(
    (run: PrvRun) => {
      handledRuns.current.add(run.id);
      setFollowedRunId(null);
      refreshRuns();
      void queryClient.invalidateQueries({ queryKey: ["prv-patterns", clientId] });
      setResultsOpened(true);
    },
    [refreshRuns, queryClient, clientId]
  );

  const handleDismissFailure = useCallback(() => {
    if (followedRunId !== null) handledRuns.current.add(followedRunId);
    setFollowedRunId(null);
    refreshRuns();
  }, [followedRunId, refreshRuns]);

  if (clientQuery.isLoading) {
    return (
      <div className="flex h-40 items-center justify-center">
        <Loader color="var(--client-color)" />
      </div>
    );
  }
  if (!clientQuery.data) {
    return <EmptyState title={t("common.failedToProcess")} />;
  }

  const runInProgress = followedRunId !== null || startRun.isPending;

  return (
    <>
      <div className="grid h-full min-h-0 grid-cols-1 gap-5 lg:grid-cols-[3fr_7fr]">
        <ReportSection
          title={t("clients.prv.config.title")}
          subtitle={t("clients.prv.config.subtitle")}
          tooltip={t("clients.prv.description")}
        >
          <PrvConfigPanel
            clientId={clientId}
            client={clientQuery.data}
            latestRun={latestRunQuery.data ?? null}
            canViewPatterns={Boolean(completedRunQuery.data)}
            runInProgress={runInProgress}
            onRunNow={() => startRun.mutate()}
            onViewPatterns={() => setResultsOpened(true)}
            onSaved={() => {
              void clientQuery.refetch();
              void queryClient.invalidateQueries({ queryKey: ["client", String(clientId)] });
            }}
          />
        </ReportSection>

        <ReportSection
          title={t("clients.prv.runLog.title")}
          subtitle={t("clients.prv.runLog.subtitle")}
        >
          <PrvRunLogTable clientId={clientId} polling={runInProgress} />
        </ReportSection>
      </div>

      <PrvRunModal
        clientId={clientId}
        runId={followedRunId}
        onCompleted={handleCompleted}
        onDismissFailure={handleDismissFailure}
      />
      <PrvResultsDrawer
        clientId={clientId}
        opened={resultsOpened}
        onClose={() => setResultsOpened(false)}
      />
    </>
  );
};

export default PrvEngineTab;
